import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { notificationPreferencesApi } from '@/lib/api';

export function SettingsNotifications() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  // Fetch notification preferences from API
  const { data: preferences, isLoading } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => notificationPreferencesApi.get(),
  });

  // Local state for form
  const [formState, setFormState] = useState({
    emailNewDiscussion: true,
    emailMention: true,
    emailTaskAssigned: true,
    emailTaskReminder: false,
    emailAnnouncement: true,
    emailFormAssigned: true,
  });

  // Sync form state with fetched preferences
  useEffect(() => {
    if (preferences) {
      setFormState({
        emailNewDiscussion: preferences.emailNewDiscussion ?? true,
        emailMention: preferences.emailMention ?? true,
        emailTaskAssigned: preferences.emailTaskAssigned ?? true,
        emailTaskReminder: preferences.emailTaskReminder ?? false,
        emailAnnouncement: preferences.emailAnnouncement ?? true,
        emailFormAssigned: preferences.emailFormAssigned ?? true,
      });
    }
  }, [preferences]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: typeof formState) => notificationPreferencesApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
  });

  const handleToggle = (key: keyof typeof formState, value: boolean) => {
    const newState = { ...formState, [key]: value };
    setFormState(newState);
    updateMutation.mutate(newState);
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="text-heading-3 text-secondary">Notification Preferences</h2>
        <p className="text-sm text-grey-500 mt-1">
          Configure how you receive notifications
        </p>

        <div className="space-y-6">
          {/* Email Notifications */}
          <div>
            <h3 className="font-medium text-secondary mb-4">Email Notifications</h3>
            <div className="space-y-3">
              <NotificationToggle
                label="Discussions"
                description="Get notified when someone selects you on a discussion or reply"
                checked={formState.emailNewDiscussion}
                onChange={(checked) => handleToggle('emailNewDiscussion', checked)}
                disabled={updateMutation.isPending}
              />
              <NotificationToggle
                label="Mentions"
                description="Get notified when someone @mentions you"
                checked={formState.emailMention}
                onChange={(checked) => handleToggle('emailMention', checked)}
                disabled={updateMutation.isPending}
              />
              <NotificationToggle
                label="Task assignments"
                description="Get notified when a task is assigned to you"
                checked={formState.emailTaskAssigned}
                onChange={(checked) => handleToggle('emailTaskAssigned', checked)}
                disabled={updateMutation.isPending}
              />
              <NotificationToggle
                label="Task due reminders"
                description="Get reminded about upcoming task deadlines"
                checked={formState.emailTaskReminder}
                onChange={(checked) => handleToggle('emailTaskReminder', checked)}
                disabled={updateMutation.isPending}
              />
              <NotificationToggle
                label="Forms"
                description="Get notified when a form is assigned to you, reopened, or submitted back to you"
                checked={formState.emailFormAssigned}
                onChange={(checked) => handleToggle('emailFormAssigned', checked)}
                disabled={updateMutation.isPending}
              />
              <NotificationToggle
                label="Announcements"
                description="Get notified when an announcement is posted to your projects"
                checked={formState.emailAnnouncement}
                onChange={(checked) => handleToggle('emailAnnouncement', checked)}
                disabled={updateMutation.isPending}
              />
            </div>

            {/* Warning when any notification is disabled */}
            {(!formState.emailNewDiscussion || !formState.emailMention || !formState.emailTaskAssigned) && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <strong>Warning:</strong> Turning off email notifications may result in delays in approvals,
                  work being blocked, or missing important project updates if you're not regularly checking Solid Hub.
                </div>
              </div>
            )}
          </div>

          <hr className="border-grey-200" />

          {/* Push Notifications - placeholder for future */}
          <div>
            <h3 className="font-medium text-secondary mb-4">Push Notifications</h3>
            <p className="text-sm text-grey-500">
              Browser and mobile push notifications coming soon.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}

function NotificationToggle({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className={cn(
      'flex items-start gap-4 cursor-pointer',
      disabled && 'opacity-50 cursor-wait'
    )}>
      <div className="relative mt-0.5">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          className="sr-only peer"
        />
        <div className={cn(
          'w-10 h-6 rounded-full transition-colors',
          checked ? 'bg-accent' : 'bg-grey-200'
        )} />
        <div className={cn(
          'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
          checked && 'translate-x-4'
        )} />
      </div>
      <div className="flex-1">
        <div className="font-medium text-secondary text-sm">{label}</div>
        <div className="text-xs text-grey-500">{description}</div>
      </div>
    </label>
  );
}
